/**
 * Which published chatbot version this build loads.
 *
 * A published version is immutable, so what a container serves stops moving
 * underneath it: the bundle is fixed at the moment this line is reviewed, and
 * going to a newer one is a one line change here, deployed like any other.
 *
 * One value for every web container and every environment. The container image
 * is built once and promoted, so a version cannot differ between environments.
 *
 * Empty still resolves to the shared, unversioned location, which is what every
 * container used before this was pinned and what the statics deploy still
 * writes. Kept working rather than removed, because it is a way back that does
 * not depend on an earlier version existing. That route lasts only as long as
 * the chatbot committed to this repository does: retiring that file leaves
 * nothing at the unversioned location, and an empty value would load nothing.
 *
 * Deliberately not held in the chatbot library itself. A value that ends up
 * inside the bundle it points at would change that bundle when it changed,
 * earning a new version, which would change the value again.
 */
export const CHATBOT_VERSION = '1.0.3';

/**
 * The value that asks for no version at all, which is not the same as absent:
 * absent falls through to the pinned version above.
 *
 * It exists for local development, where the chatbot is served by the container
 * itself out of its own public directory. Nothing versioned is ever written
 * there, so a version segment would ask for a path that only exists on a
 * content host. config/next/config.development.js sets this, and reaches only
 * the dev server: a container build runs with NODE_ENV=production, where that
 * configuration returns everything untouched.
 */
export const UNVERSIONED = 'unversioned';

/**
 * What this build was told to load, rather than what it would pick on its own.
 * Either a hosted pull request's own chatbot, so a preview shows the branch, or
 * the marker above.
 *
 * It arrives through the shared container configuration, which resolves it and
 * bakes it in, because neither value can be written into a commit: a preview
 * identifier is decided per pipeline run, and whether a build is the dev server
 * is not a property of the source.
 *
 * Written out in full rather than destructured or indexed, because the
 * substitution the framework performs is textual and only replaces this shape.
 */
const BUILD_VERSION = process.env.NEXT_PUBLIC_CHATBOT_VERSION || '';

/**
 * Which version wins, in the order the cases are tried: an explicit request for
 * none, then what the build was told, then the pinned version.
 */
const resolveVersion = (): string => {
  if (BUILD_VERSION === UNVERSIONED) return '';
  if (BUILD_VERSION) return BUILD_VERSION;
  return CHATBOT_VERSION;
};

const RESOLVED_VERSION = resolveVersion();

/**
 * Where the chatbot bundle lives, composed from the content host and the version.
 *
 * Both shapes are written out rather than composed from an optional segment, so
 * that the one way to get this wrong cannot be written here at all: an empty
 * version interpolated into a single template gives `chatbot//chatbot.js`, a
 * different location, and it fails quietly because some hosts collapse the
 * doubled separator and some do not.
 *
 * The two values stay separate and keep their own owners. The prefix answers
 * which content host to use, differs per environment and is set outside this
 * repository. The version belongs to this repository and applies after it.
 *
 * The bundle finds its own images from here without being told: it captures its
 * script location at load time and resolves them as siblings. Fonts are the
 * exception, resolving through the prefix instead, so a build on an older
 * version uses whichever fonts are currently live.
 */
export const chatbotScriptSrc = (staticPrefix: string, version: string = RESOLVED_VERSION): string => {
  if (!version) {
    return `${staticPrefix}/chatbot/chatbot.js`;
  }

  return `${staticPrefix}/chatbot/${version}/chatbot.js`;
};
